import { useCallback, useMemo } from "react";

import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { useRosettaConfig } from "~/lib/app/hooks";

import { FallbackPricing } from "./consts";
import type { BillingCycle, Bundle, Plan, Variant } from "./types";

export const usePricing = () => {
  const { rosettaConfigState } = useRosettaConfig();
  const pricing = useMemo(
    () => rosettaConfigState.result?.posties?.pricing ?? FallbackPricing,
    [rosettaConfigState.result?.posties?.pricing],
  );
  return { pricing };
};

export const useTracking = (variant: Variant) => {
  const triggerPoint = variant === "brochure" ? "Posties brochure" : "Posties paywall";

  const trackBillingCycleToggle = useCallback(
    (cycle: BillingCycle) => {
      sendGATracking({
        action: `Subscribe to SCMP/${triggerPoint}/Plan term toggle/Click` as const,
        category: "Subscribe to SCMP",
        label: cycle === "annual" ? "Yearly" : "Monthly",
      });
    },
    [triggerPoint],
  );

  const trackPlanSelected = useCallback(
    (plan: Plan) => {
      const { billingCycle, bundle } = plan;
      const pianoTermNames: Record<`${Bundle}|${BillingCycle}`, string> = {
        "epaper|annual": "Annual: Posties ePaper + Digital",
        "epaper|monthly": "Monthly: Posties ePaper + Digital",
        "print|annual": "Annual: Posties Print + Digital",
        "print|monthly": "Monthly: Posties Print + Digital",
      };
      const label = pianoTermNames[`${bundle}|${billingCycle}`];

      sendGATracking({
        action: `Subscribe to SCMP/${triggerPoint}/Plans/Click` as const,
        category: "Subscribe to SCMP",
        label,
      });
    },
    [triggerPoint],
  );

  const trackCompareBenefitClick = useCallback(() => {
    sendGATracking({
      action: `Subscribe to SCMP/${triggerPoint}/Compare benefits/Click` as const,
      category: "Subscribe to SCMP",
      label: "",
    });
  }, [triggerPoint]);

  const trackSubscribeClick = useCallback(() => {
    sendGATracking({
      action: `Subscribe to SCMP/${triggerPoint}/Subscribe/Click` as const,
      category: "Subscribe to SCMP",
      label: "",
    });
  }, [triggerPoint]);

  const trackRedeemClick = useCallback(() => {
    sendGATracking({
      action: `Subscribe to SCMP/${triggerPoint}/Redeem/Click` as const,
      category: "Subscribe to SCMP",
      label: "",
    });
  }, [triggerPoint]);

  return {
    trackBillingCycleToggle,
    trackCompareBenefitClick,
    trackPlanSelected,
    trackRedeemClick,
    trackSubscribeClick,
  };
};
